import { PartialWithId } from '@inmagik/react-crud/dist/types'
import classNames from 'classnames'
import { Formik, FormikErrors, useFormikContext } from 'formik'
import { useEffect, useRef, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import FormBilancia, { BILANCIA_STEPS } from './FormBilancia'

interface ModalWizardBilanciaProps<T> {
  isOpen: boolean
  toggle: () => void
  title: string
  initialValues: PartialWithId<T>
  schema: any
  save: (record: PartialWithId<T>, actions: any) => Promise<any>
}

function firstStepWithErrors(errors: FormikErrors<unknown>) {
  const names = Object.keys(errors)
  return BILANCIA_STEPS.findIndex((s) => s.fields.some((f) => names.includes(f)))
}

// Creazione bilancia su mobile: modale a schermo intero con il form diviso in passaggi.
// "Avanti" valida solo i campi del passaggio corrente
export default function ModalWizardBilancia<T>({
  isOpen,
  toggle,
  title,
  initialValues,
  schema,
  save,
}: ModalWizardBilanciaProps<T>) {
  return (
    <Formik initialValues={initialValues} onSubmit={save} validationSchema={schema} enableReinitialize>
      <Modal show={isOpen} onHide={toggle} fullscreen className="modal-wizard-bilancia">
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <WizardContent toggle={toggle} />
      </Modal>
    </Formik>
  )
}

function WizardContent({ toggle }: { toggle: () => void }) {
  const { validateForm, setTouched, touched, handleSubmit, isSubmitting, errors, submitCount } =
    useFormikContext<unknown>()
  const [step, setStep] = useState(0)
  const bodyRef = useRef<HTMLDivElement>(null)
  const isLast = step === BILANCIA_STEPS.length - 1

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 })
  }, [step])

  // Dopo un salvataggio non riuscito (validazione o errori dal server) torna al primo passaggio con errori
  useEffect(() => {
    if (submitCount === 0 || isSubmitting) return
    const errorStep = firstStepWithErrors(errors)
    if (errorStep !== -1) setStep(errorStep)
  }, [submitCount, isSubmitting])

  const next = async () => {
    const fields = BILANCIA_STEPS[step].fields
    const stepErrors = await validateForm()
    setTouched({ ...touched, ...Object.fromEntries(fields.map((f) => [f, true])) }, false)
    if (!fields.some((f) => f in stepErrors)) setStep(step + 1)
  }

  return (
    <>
      <Modal.Body ref={bodyRef}>
        <div className="wizard-progress mb-3">
          <div className="d-flex gap-1 mb-2">
            {BILANCIA_STEPS.map((s, i) => (
              <button
                key={s.title}
                type="button"
                aria-label={s.title}
                className={classNames('wizard-progress-segment', { active: i <= step })}
                disabled={i >= step}
                onClick={() => setStep(i)}
              />
            ))}
          </div>
          <div className="text-body-secondary p-sm">
            Passo {step + 1} di {BILANCIA_STEPS.length}
          </div>
          <h5 className="heading-h5 mb-0">{BILANCIA_STEPS[step].title}</h5>
        </div>
        <FormBilancia step={step} />
      </Modal.Body>
      <Modal.Footer className="d-flex flex-nowrap">
        <Button
          type="button"
          variant="outline-secondary"
          className="flex-fill"
          onClick={() => (step === 0 ? toggle() : setStep(step - 1))}
        >
          {step === 0 ? 'Chiudi' : 'Indietro'}
        </Button>
        {isLast ? (
          <Button
            type="button"
            variant="secondary"
            className="flex-fill text-white"
            disabled={isSubmitting}
            onClick={() => handleSubmit()}
          >
            Salva
          </Button>
        ) : (
          <Button type="button" variant="secondary" className="flex-fill text-white" onClick={next}>
            Avanti
          </Button>
        )}
      </Modal.Footer>
    </>
  )
}
